import AccountTreeOutlined from "@mui/icons-material/AccountTreeOutlined";
import ArrowForward from "@mui/icons-material/ArrowForward";
import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import Check from "@mui/icons-material/Check";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Close from "@mui/icons-material/Close";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import EditNoteOutlined from "@mui/icons-material/EditNoteOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import InsightsOutlined from "@mui/icons-material/InsightsOutlined";
import LinkOutlined from "@mui/icons-material/LinkOutlined";
import TouchAppOutlined from "@mui/icons-material/TouchAppOutlined";
import VerifiedUserOutlined from "@mui/icons-material/VerifiedUserOutlined";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Radio,
  Stack,
  Step,
  StepLabel,
  Stepper,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { usePendo } from "../analytics/hooks/use-pendo-watch";
import { Logo } from "../generic/components/logo";
import { AssistantLogo } from "./assistant-logo";
import { ASSISTANTS, STARTER_PROMPTS, assistantInfo, dataSharingAgreement } from "./assistants";
import { UrlText } from "./url-text";

import type { Assistant, SetupField } from "./assistants";
import type { ConnectionUrl } from "./connection-url";
import type { FC, ReactNode } from "react";

const starterIcons: Record<string, ReactNode> = {
  create_comms: <EditNoteOutlined fontSize="small" />,
  build_program: <AccountTreeOutlined fontSize="small" />,
  engagement: <InsightsOutlined fontSize="small" />,
  chain: <LinkOutlined fontSize="small" />,
};

const SidePanel: FC<{ icon: ReactNode; title: string; children: ReactNode }> = ({
  icon,
  title,
  children,
}) => (
  <Stack
    component="aside"
    spacing={1.5}
    sx={{
      width: { md: 300 },
      flexShrink: 0,
      overflowY: { md: "auto" },
      p: 3,
      bgcolor: "action.hover",
      borderLeft: { md: 1 },
      borderTop: { xs: 1, md: 0 },
      borderColor: "divider",
    }}
  >
    <Typography
      variant="subtitle2"
      sx={{ display: "flex", alignItems: "center", gap: 1, color: "primary.main" }}
    >
      {icon}
      {title}
    </Typography>
    {children}
  </Stack>
);

const SideItem: FC<{ heading: string; body: string }> = ({ heading, body }) => (
  <Box>
    <Typography variant="body2" sx={{ fontWeight: 700 }}>
      {heading}
    </Typography>
    <Typography variant="body2" color="textSecondary">
      {body}
    </Typography>
  </Box>
);

const StepHeading: FC<{ title: string; body: string }> = ({ title, body }) => (
  <Box>
    <Typography variant="h5" component="h2" sx={{ mb: 1 }}>
      {title}
    </Typography>
    <Typography variant="body2" color="textSecondary">
      {body}
    </Typography>
  </Box>
);

const Field: FC<{ field: SetupField; connection: ConnectionUrl | null }> = ({
  field,
  connection,
}) => (
  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", fontSize: 13 }}>
    <Typography variant="body2" color="textSecondary" sx={{ minWidth: 120 }}>
      {field.label}
    </Typography>
    {"connectionUrl" in field ? (
      connection ? (
        <Typography variant="body2" component="span">
          <UrlText connection={connection} />
        </Typography>
      ) : (
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          Your connection URL
        </Typography>
      )
    ) : (
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {field.value}
      </Typography>
    )}
  </Box>
);

interface ConnectWizardProps {
  initialAssistant: Assistant;
  connection: ConnectionUrl | null;
  agreement: { by: string; on: string } | null;
  canAgree: boolean;
  onAgree: (assistant: Assistant) => Promise<boolean | undefined>;
  onComplete: (assistant: Assistant) => Promise<void | undefined>;
  onClose: () => void;
}

export const ConnectWizard: FC<ConnectWizardProps> = ({
  initialAssistant,
  connection,
  agreement,
  canAgree,
  onAgree,
  onComplete,
  onClose,
}) => {
  const pendo = usePendo();
  const [step, setStep] = useState(0);
  const [assistant, setAssistant] = useState(initialAssistant);
  const [pathIndex, setPathIndex] = useState(0);
  const [ticked, setTicked] = useState<Record<string, boolean>>({});
  const [urlCopied, setUrlCopied] = useState(false);
  const [copiedPrompt, setCopiedPrompt] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const tool = assistantInfo(assistant);
  const path = tool.paths[Math.min(pathIndex, tool.paths.length - 1)]!;
  const tickKey = (index: number): string => `${assistant}-${path.id}-${index}`;
  const tickedCount = path.steps.filter((_, index) => ticked[tickKey(index)]).length;

  const save = async (action: () => Promise<void>, message: string): Promise<void> => {
    setSaving(true);
    setSaveError(null);
    try {
      await action();
    } catch (error) {
      console.error(message, error);
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  };

  const agree = (): Promise<void> =>
    save(async () => {
      const recorded = await onAgree(assistant);
      pendo?.track("mcp-connect-agreement-continued", {
        assistant,
        agreement_recorded: recorded ?? false,
      });
      setStep(1);
    }, "Couldn’t save your agreement. Please try again.");

  const complete = (): Promise<void> =>
    save(async () => {
      await onComplete(assistant);
      pendo?.track("mcp-connect-setup-completed", {
        assistant,
        path: path.id,
        steps_ticked: tickedCount,
        steps_total: path.steps.length,
      });
      setStep(2);
    }, "Couldn’t save your progress. Please try again.");

  const copyUrl = (): void => {
    if (!connection) return;
    void navigator.clipboard.writeText(connection.url).catch(console.error);
    pendo?.track("mcp-connect-url-copied", { assistant });
    setUrlCopied(true);
  };

  const copyPrompt = (id: string, text: string): void => {
    void navigator.clipboard.writeText(text).catch(console.error);
    pendo?.track("mcp-connect-prompt-copied", { assistant, prompt: id });
    setCopiedPrompt(id);
  };

  const done = (): void => {
    pendo?.track("mcp-connect-done", { assistant });
    onClose();
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xl" fullWidth aria-labelledby="mcp-connect-title">
      <DialogTitle
        id="mcp-connect-title"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Logo sx={{ width: 24, height: 24 }} />
        Connect your AI assistant
        <Chip label="Beta" size="small" color="primary" variant="outlined" />
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ ml: "auto" }}
          data-analytics-id="mcp-connect-close"
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: "divider" }}>
        <Stepper activeStep={step}>
          {["Choose", `Add to ${tool.name}`, "Try it"].map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          overflow: { md: "hidden" },
        }}
      >
        <Stack
          spacing={3}
          sx={{ flex: 1, minWidth: 0, overflowY: { md: "auto" }, p: { xs: 2, md: 4 } }}
        >
          {saveError ? <Alert severity="error">{saveError}</Alert> : null}

          {step === 0 ? (
            <>
              <StepHeading
                title="Which assistant does your team use?"
                body="Once it’s connected, you can ask it to draft comms, build programs and report on engagement in ChangeEngine."
              />
              <Stack component="fieldset" spacing={1.5} sx={{ border: 0, p: 0, m: 0 }}>
                <Box component="legend" sx={{ typography: "subtitle2", mb: 1 }}>
                  AI assistant
                </Box>
                {ASSISTANTS.map((option) => (
                  <Box
                    key={option.id}
                    component="label"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      p: 2,
                      border: 1,
                      borderRadius: 2,
                      cursor: "pointer",
                      borderColor: option.id === assistant ? "primary.main" : "divider",
                      bgcolor: option.id === assistant ? "action.selected" : "background.paper",
                    }}
                  >
                    <Radio
                      name="mcp-connect-assistant"
                      checked={option.id === assistant}
                      onChange={() => {
                        setAssistant(option.id);
                        setPathIndex(0);
                        setUrlCopied(false);
                      }}
                    />
                    <AssistantLogo assistant={option} size={40} />
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {option.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {option.plan}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </>
          ) : null}

          {step === 1 ? (
            <>
              <StepHeading
                title={`Add ChangeEngine to ${tool.name}`}
                body={`These steps happen in ${tool.name}. Keep this window open and tick each one off as you go.`}
              />
              <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Your connection URL
                </Typography>
                {connection ? (
                  <>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2}
                      sx={{ alignItems: { sm: "center" } }}
                    >
                      <Typography variant="body1" sx={{ flex: 1, minWidth: 0 }}>
                        <UrlText connection={connection} />
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={urlCopied ? <Check /> : <ContentCopyOutlined />}
                        onClick={copyUrl}
                        data-analytics-id="mcp-connect-copy-url"
                      >
                        {urlCopied ? "Copied" : "Copy URL"}
                      </Button>
                    </Stack>
                    <Typography variant="caption" color="textSecondary">
                      Unique to your workspace —{" "}
                      <Box component="strong">{connection.workspace}</Box> is filled in for you.
                    </Typography>
                  </>
                ) : (
                  <Alert severity="info">
                    Contact your ChangeEngine team for your connection URL
                  </Alert>
                )}
              </Box>

              {tool.paths.length > 1 ? (
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={pathIndex}
                  aria-label="Plan"
                  onChange={(_, value: number | null) => {
                    if (value !== null) setPathIndex(value);
                  }}
                >
                  {tool.paths.map((option, index) => (
                    <ToggleButton
                      key={option.id}
                      value={index}
                      data-analytics-id={`mcp-connect-path-${option.id}`}
                    >
                      {option.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              ) : null}

              <Stack component="ol" spacing={2} sx={{ listStyle: "none", m: 0, p: 0 }}>
                {path.steps.map((setupStep, index) => {
                  const isTicked = Boolean(ticked[tickKey(index)]);
                  return (
                    <Box key={setupStep.text} component="li" sx={{ display: "flex", gap: 2 }}>
                      <IconButton
                        aria-pressed={isTicked}
                        aria-label={`Mark step ${index + 1} as done`}
                        data-analytics-id="mcp-connect-tick-step"
                        size="small"
                        onClick={() =>
                          setTicked((old) => ({ ...old, [tickKey(index)]: !isTicked }))
                        }
                        sx={{
                          width: 32,
                          height: 32,
                          flexShrink: 0,
                          fontSize: 14,
                          fontWeight: 700,
                          border: 1,
                          borderColor: isTicked ? "success.main" : "primary.light",
                          bgcolor: isTicked ? "success.main" : "action.selected",
                          color: isTicked ? "success.contrastText" : "primary.main",
                          "&:hover": { bgcolor: isTicked ? "success.dark" : "action.focus" },
                        }}
                      >
                        {isTicked ? <Check fontSize="small" /> : index + 1}
                      </IconButton>
                      <Stack spacing={1} sx={{ pt: 0.5, minWidth: 0 }}>
                        <Typography
                          component="div"
                          variant="body2"
                          color={isTicked ? "textSecondary" : "textPrimary"}
                        >
                          {setupStep.text}
                          {setupStep.admin ? (
                            <Chip
                              label="Admin"
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{ ml: 1, height: 20 }}
                            />
                          ) : null}
                        </Typography>
                        {setupStep.fields ? (
                          <Stack
                            spacing={0.5}
                            sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}
                          >
                            {setupStep.fields.map((field) => (
                              <Field key={field.label} field={field} connection={connection} />
                            ))}
                          </Stack>
                        ) : null}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </>
          ) : null}

          {step === 2 ? (
            <>
              <StepHeading
                title={`Try your first request in ${tool.name}`}
                body={`Open ${tool.name}, make sure ChangeEngine is switched on, and paste one of these. Everything it creates lands in ChangeEngine as a draft.`}
              />
              <Stack spacing={1.5}>
                {STARTER_PROMPTS.map((prompt) => (
                  <Stack
                    key={prompt.id}
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 2,
                      alignItems: { sm: "center" },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        color="primary"
                        sx={{ display: "flex", alignItems: "center", gap: 0.5, fontWeight: 700 }}
                      >
                        {starterIcons[prompt.id]}
                        {prompt.kind}
                      </Typography>
                      <Typography variant="body2">“{prompt.text}”</Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={copiedPrompt === prompt.id ? <Check /> : <ContentCopyOutlined />}
                      onClick={() => copyPrompt(prompt.id, prompt.text)}
                      data-analytics-id="mcp-connect-copy-prompt"
                    >
                      {copiedPrompt === prompt.id ? "Copied" : "Copy"}
                    </Button>
                  </Stack>
                ))}
              </Stack>
              <Alert severity="info" icon={<InfoOutlined />}>
                Can’t see ChangeEngine in {tool.name}? Go back a step and check each one is done —
                you can come back to this page from Settings › ChangeEngine MCP at any time.
              </Alert>
            </>
          ) : null}
        </Stack>

        {step === 0 ? (
          <SidePanel icon={<VerifiedUserOutlined fontSize="small" />} title="Sharing your data">
            {agreement ? (
              <>
                <Typography
                  variant="body2"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    color: "success.dark",
                    fontWeight: 600,
                  }}
                >
                  <CheckCircle fontSize="small" />
                  Agreed by {agreement.by} on {agreement.on}
                </Typography>
                <Typography variant="body2">
                  Data sharing is already agreed for your organization. It covers every AI assistant
                  your team connects, including {tool.name}, so there’s nothing more to accept.
                </Typography>
                <Typography variant="body2">
                  Your organization is the data controller for anything shared with {tool.name}, and{" "}
                  {tool.name}’s own terms apply to it.
                </Typography>
              </>
            ) : (
              <>
                {dataSharingAgreement(assistant).map((paragraph) => {
                  const [before, after] = paragraph.split("Continue");
                  return (
                    <Typography key={paragraph} variant="body2">
                      {after === undefined ? (
                        paragraph
                      ) : (
                        <>
                          {before}
                          <strong>Continue</strong>
                          {after}
                        </>
                      )}
                    </Typography>
                  );
                })}
                {canAgree ? null : (
                  <Alert severity="warning">A customer admin must agree to this.</Alert>
                )}
              </>
            )}
          </SidePanel>
        ) : null}

        {step === 1 ? (
          <SidePanel icon={<TouchAppOutlined fontSize="small" />} title="This part is manual">
            <SideItem
              heading={`Done in ${tool.name}`}
              body={`ChangeEngine can’t add itself to ${tool.name}. Follow each step there, then tick it off here.`}
            />
            <SideItem
              heading="Not an admin?"
              body="Steps marked Admin need someone with admin rights. Copy the URL and send them these steps."
            />
            <SideItem
              heading="Can’t find a menu?"
              body={`Menus move. Search ${tool.name}’s settings for “connector” or “app”.`}
            />
          </SidePanel>
        ) : null}

        {step === 2 ? (
          <SidePanel icon={<AutoAwesomeOutlined fontSize="small" />} title="Good to know">
            <SideItem
              heading="It knows your account"
              body="Say “the London team” or “our onboarding program” — no need to explain."
            />
            <SideItem
              heading="Ask what it can do"
              body="“What can you do in ChangeEngine?” gets a tour."
            />
            <SideItem
              heading="Review in ChangeEngine"
              body="Everything it makes shows up as a draft with full history."
            />
          </SidePanel>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: "divider" }}>
        <Typography variant="body2" color="textSecondary" sx={{ mr: "auto" }}>
          {step === 1
            ? `${tickedCount} of ${path.steps.length} steps ticked off`
            : `Step ${step + 1} of 3`}
        </Typography>
        {step > 0 ? (
          <Button
            disabled={saving}
            data-analytics-id="mcp-connect-back"
            onClick={() => {
              setSaveError(null);
              setStep(step - 1);
            }}
          >
            Back
          </Button>
        ) : null}
        {step === 0 ? (
          <Button
            variant="contained"
            endIcon={<ArrowForward />}
            disabled={saving || !canAgree}
            onClick={() => void agree()}
            data-analytics-id="mcp-connect-continue"
          >
            Continue
          </Button>
        ) : null}
        {step === 1 ? (
          <Button
            variant="contained"
            endIcon={<ArrowForward />}
            disabled={saving}
            onClick={() => void complete()}
            data-analytics-id="mcp-connect-added-it"
          >
            I’ve added it
          </Button>
        ) : null}
        {step === 2 ? (
          <Button variant="contained" onClick={done} data-analytics-id="mcp-connect-done">
            Done
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};
